import React from 'react';
import {Controller} from 'react-hook-form';
import {ErrorMessage} from '@hookform/error-message';
import Label from './Label';
import {twMerge} from 'tailwind-merge';
import {useFormContext} from './context';

/**
 * FormField - a flexible field wrapper for react-hook-form
 *
 * Props:
 * - name: string (required)
 * - label: string | ReactNode
 * - form: react-hook-form object (from useForm)
 * - description: string | ReactNode (optional)
 * - children: field component (Input, Select, MultiSelect, etc)
 * - rules: validation rules (optional)
 * - ...rest: any other props
 */

function isFieldRequired(schema, fieldPath) {
	// Safety check: fieldPath must be a string
	if (!fieldPath || typeof fieldPath !== 'string') {
		return false;
	}

	// Unwrap ZodEffects (from .refine, .transform, etc) to get to the actual schema
	let unwrappedSchema = schema;
	while (unwrappedSchema?._def?.typeName === 'ZodEffects') {
		unwrappedSchema = unwrappedSchema._def.schema;
	}

	// Using 'refine' with zod-object causes the shape to be in a different place
	if (!unwrappedSchema?.shape && !unwrappedSchema?._def?.schema?.shape) return false;

	// Split the path into parts (e.g., "calendarEvent.startsAt" -> ["calendarEvent", "startsAt"])
	const parts = fieldPath.split('.');

	let currentShape = unwrappedSchema.shape || unwrappedSchema._def.schema.shape;
	let field = null;

	// Traverse the schema shape following the path
	for (const part of parts) {
		field = currentShape[part];
		if (!field) return false;

		// If this is a nested object, go deeper
		if (field._def?.typeName === 'ZodObject') {
			currentShape = field.shape;
		}
	}

	// Now check the actual field
	if (!field) return false;

	// Unwrap ZodEffects on the field itself (e.g., from field-level .refine())
	while (field?._def?.typeName === 'ZodEffects') {
		field = field._def.schema;
	}

	// If optional or nullable, not required
	if (field.isOptional?.() || field.isNullable?.()) return false;

	// If boolean, not required (checkboxes are always present)
	if (field._def?.typeName === 'ZodBoolean') return false;

	// If array, not required by default
	if (field._def?.typeName === 'ZodArray') {
		const hasMinRequirement = field._def?.minLength?.value > 0;
		const hasExactLength = field._def?.exactLength?.value > 0;
		return hasMinRequirement || hasExactLength;
	}

	// If string, required if .min(1)
	if (field._def?.typeName === 'ZodString') {
		const minCheck = field._def.checks?.find(c => c.kind === 'min');
		return minCheck && minCheck.value > 0;
	}

	// If number, required if .min(1)
	if (field._def?.typeName === 'ZodNumber') {
		const minCheck = field._def.checks?.find(c => c.kind === 'min');
		return minCheck && minCheck.value > 0;
	}

	return false;
}

const FormField = ({
	children,
	name,
	label,
	className,
	description,
	errors,
	wrapLabel = false,
}) => (
	<div
		className={twMerge(
			wrapLabel ? 'inline-flex flex-col gap-1' : 'flex flex-col gap-1',
			className,
		)}
	>
		{!wrapLabel && label}
		{wrapLabel ? (
			<label className="inline-flex items-center gap-2" htmlFor={name}>
				{children}
				{label && label}
			</label>
		) : (
			children
		)}
		<ErrorMessage
			name={name}
			errors={errors}
			render={({message, messages}) =>
				messages ? (
					messages.map((message, index) => (
						<p key={index} className="text-red-500 text-xs">
							{message}
						</p>
					))
				) : (
					<p className="text-red-500 text-xs">{message}</p>
				)
			}
		/>
		{description && (
			<p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
		)}
	</div>
);

const Field = ({
	name,
	label,
	labelClassName,
	description,
	children,
	rules,
	className,
	type,
	...rest
}) => {
	const {form, schema} = useFormContext();
	const errors = form?.formState?.errors;
	const isRequired = isFieldRequired(schema, name);

	const renderLabel = () => {
		return (
			<Label htmlFor={name} className={labelClassName}>
				{label} {isRequired && label && <span className="text-red-500">*</span>}
			</Label>
		);
	};

	if (type === 'checkbox') {
		// If controlled checkbox (has checked prop), don't use form.register
		if (rest.checked !== undefined) {
			return (
				<FormField
					wrapLabel
					label={renderLabel()}
					description={description}
					errors={errors}
					className={className}
				>
					<input type="checkbox" id={name} {...rest} />
				</FormField>
			);
		}

		return (
			<FormField
				wrapLabel
				label={renderLabel()}
				description={description}
				errors={errors}
				className={className}
			>
				<input type="checkbox" {...form.register(name, rules)} id={name} {...rest} />
			</FormField>
		);
	}

	// If form.control is provided, use Controller for custom components
	if (form?.control) {
		// Try to detect if the child is a custom component (not native input)
		// We'll use Controller for all cases except when the child is a native input/textarea/select
		const childType = children.type?.displayName || children.type?.name || '';
		const isNative = ['input', 'textarea', 'select'].includes(childType.toLowerCase());
		if (!isNative) {
			return (
				<FormField
					name={name}
					label={renderLabel()}
					errors={errors}
					className={className}
				>
					<Controller
						name={name}
						control={form.control}
						rules={rules}
						render={({field}) => {
							const childOnChange = children?.props?.onChange;
							const userOnChange = rest?.onChange || childOnChange;

							const composed = {
								...rest,
								id: name,
								...field, // value/onChange from Controller
							};
							// Compose onChange so custom handlers still run alongside RHF updates
							if (userOnChange) {
								const fieldOnChange = field.onChange;
								composed.onChange = (...args) => {
									fieldOnChange?.(...args);
									userOnChange?.(...args);
								};
							}
							return React.cloneElement(children, composed);
						}}
					/>
				</FormField>
			);
		}
	}

	// Otherwise, use register for native inputs
	// Compose onChange and onBlur for native inputs so custom handlers run and RHF still updates state
	const registered = form.register(name, rules);
	const childOnChange = children?.props?.onChange;
	const childOnBlur = children?.props?.onBlur;
	const userOnChange = rest?.onChange || childOnChange;
	const userOnBlur = rest?.onBlur || childOnBlur;
	const composedProps = {
		...registered,
		id: name,
		...rest,
	};
	if (userOnChange) {
		const registeredOnChange = registered.onChange;
		composedProps.onChange = (...args) => {
			registeredOnChange?.(...args);
			userOnChange?.(...args);
		};
	}
	if (userOnBlur) {
		const registeredOnBlur = registered.onBlur;
		composedProps.onBlur = (...args) => {
			registeredOnBlur?.(...args);
			userOnBlur?.(...args);
		};
	}
	return (
		<FormField name={name} label={renderLabel()} errors={errors} className={className}>
			{React.cloneElement(children, composedProps)}
		</FormField>
	);
};

export default Field;
